import React, { useEffect, useMemo, useRef } from "react";

import {
  formatFractionInput,
  formatPercentageInput,
  inputStateForValue,
  isNullOrUndefined,
  isPercentageComplete,
  isValidNonnegativeNumber,
  isValidPercentageDecimal,
  isValidPositiveNumber,
  queryIsLoading,
  useGetCarbonProgramByFarmId,
  useGetFootprintingPigletFeedInformationByPrimaryDataId,
  useGetFootprintingPigletFootprint,
  useGetPigletFeedTypes,
  useKeyedFormList,
  useMutateFootprintingPigletFeedInformation,
  usePracticeManagementPigletParameters,
  useSaveFormOnUnmount,
  useTranslation,
} from "utils";

import { DEFAULT_DRY_MATTER_PERCENTAGE, HARD_INPUT_STATES, INPUT_STATE, KG_PER_TONNE, TOO_HIGH_INPUT_STATES } from "consts";

import { PigletFeedType, UploadablePigletFeedInformation } from "models";

import {
  AlertBanner,
  Button,
  Card,
  Flex,
  Form,
  FormInstance,
  Icon,
  IconButton,
  InputImageCard,
  InputNumber,
  Option,
  PageSpin,
  ProgressBarCardWithContent,
  SanityCheck,
  Select,
  Switch,
  Tag,
  Typography,
} from "components";

interface PigletFeedInfoFormProps {
  primaryDataId?: string;
  readOnly?: boolean;
  farmId: string;
  year: number;
}

const createFeedTypePlaceholder = (): UploadablePigletFeedInformation => ({
  id: undefined,
  feedId: "",
  isSelfProduced: null,
  totalAmountFedTs: null,
  silagePct: null,
  energyValue: null,
  dryMatterPct: null,
  pigletsPct: null,
  sowsPct: null,
  emissionFactorKgCo2Eq: null,
});

const getEnergyValuesFromFeedType = (feedType: PigletFeedType | undefined): { energyValue: number | null } => {
  if (!feedType) return { energyValue: null };
  return { energyValue: feedType.energyMjMeKgDm ?? null };
};

// Unit is MJ ME / kg weight gain per piglet reared. TLL Jena, Schweinefütterung Heft 3/2011, Tab. 14
const EXPECTED_FEED_ENERGY_PER_KG_GAIN_PER_PIGLET: PracticeManagement.ParameterBounds = [-Infinity, 22, 30, Infinity];

// Both checks below read totalAmountFedTs as tonnes fed as-is (fresh weight), then convert to kg dry
// matter via dryMatterPct before pairing with energyMjMeKgDm.

// Typical energy density of piglet rearing feed, MJ ME per kg dry matter.
const TYPICAL_FEED_ENERGY_MJ_PER_KG_DM = 13.5;

// Feed conversion ratio in rearing, kg dry matter / kg weight gain.
// The two piglet ratios are the same measurement scaled by feed energy density, so this band is derived
// from the energy band rather than taken from the as-fed figures reported by LfL (Ø 1.8) and LWK NRW
// (1.61-1.71) — using those directly leaves the two checks contradicting each other on a normal farm.
// At the typical density the two are equivalent; they diverge only when a farm's feed is unusually
// energy dense or dilute, which is exactly what this second ratio is here to catch.
const EXPECTED_PIGLET_FCR: PracticeManagement.ParameterBounds = [
  -Infinity,
  EXPECTED_FEED_ENERGY_PER_KG_GAIN_PER_PIGLET[1] / TYPICAL_FEED_ENERGY_MJ_PER_KG_DM,
  EXPECTED_FEED_ENERGY_PER_KG_GAIN_PER_PIGLET[2] / TYPICAL_FEED_ENERGY_MJ_PER_KG_DM,
  Infinity,
];

// Sow feed energy as a fraction of the yearly energy requirement calculated for the herd.
// ±15% as agreed with the domain team, superseding the ±10% of the reference model.
const EXPECTED_SOW_ENERGY_REQUIREMENT_RATIO: PracticeManagement.ParameterBounds = [-Infinity, 0.85, 1.15, Infinity];

const SOW_AND_PIGLET_DEFAULTS = {
  avgPigletWeightAtBirthKg: 1.4,
  avgPigletWeightAtWeaningEndKg: 8.5,
  avgPigletWeightGainKg: 7.1, // End weight (8.5) - Start Weight (1.4)
  totalGestationDays: 115, // total gestation length
  lowGestationDays: 87, // 115 - last 4 weeks
  lowGestationDailyMJ: 34, // MJ ME/day
  highGestationDays: 28, // last 4 weeks
  highGestationDailyMJ: 42, // MJ ME/day
  gestationEnergyRequirementBase: 87 * 34 + 28 * 42, // lowDays * lowMJ + highDays * highMJ
  emptyDailyMJ: 32, // Energy used per day when sow is not pregnant
  lactationDays: 26, // Total days after birth when the sow is milk-feeding piglets
  lactationMaintenanceMJ: 25, // MJ ME/day
  lactationEnergyBase: 650, // 26 days * 25 MJ/day => 650 MJ
  milkEnergyPerKgLitterGainMJ: 16, // net feed ME per kg litter gain, allowing for body-reserve
};

interface FeedEnergyCheckCardProps {
  dataTestId: string;
  title: string;
  description: string;
  isExpected: boolean;
  alertTitle: string;
  alertDescription: string;
}

const FeedEnergyCheckCard = ({ dataTestId, title, description, isExpected, alertTitle, alertDescription }: FeedEnergyCheckCardProps) => (
  <Card border dataTestId={dataTestId} className="padding-200">
    <Flex vertical gap={32}>
      <Flex vertical className="padding-bottom-0" gap={8}>
        <Typography variant="h3">{title}</Typography>
        <Typography variant="paragraph-m" color="grey-600">
          {description}
        </Typography>
      </Flex>

      <AlertBanner
        type={isExpected ? "success" : "warning"}
        iconName={isExpected ? "tabler/confetti" : "tabler/info-circle"}
        iconAlign="start"
        iconSize={24}
        headingSize="h6"
        title={alertTitle}
        description={alertDescription}
      />
    </Flex>
  </Card>
);

const PigletFeedInformationForm = ({ primaryDataId, readOnly, farmId, year }: PigletFeedInfoFormProps) => {
  const { t } = useTranslation(["footprintingPiglet", "piglet"]);
  const formRef = useRef<FormInstance>(null);

  const { data: carbonProgram } = useGetCarbonProgramByFarmId(farmId);
  const pigletFootprintQuery = useGetFootprintingPigletFootprint(farmId, year);
  const { data: pigletFootprint } = pigletFootprintQuery;
  const feedInformationDataQuery = useGetFootprintingPigletFeedInformationByPrimaryDataId(primaryDataId ?? "");
  const { data: feedInformationQuery } = feedInformationDataQuery;
  const pigletFeedsDataQuery = useGetPigletFeedTypes();
  const { data: pigletFeedsQuery } = pigletFeedsDataQuery;
  const pigletPracticeQuery = usePracticeManagementPigletParameters(carbonProgram?.country?.toLowerCase() as PracticeManagement.CountryCode);
  const isLoading = [feedInformationDataQuery, pigletFeedsDataQuery, pigletFootprintQuery, pigletPracticeQuery].some(queryIsLoading);
  const keyedFeedInformations = useKeyedFormList<UploadablePigletFeedInformation>(formRef, { isReady: !isLoading });

  const selectGroupName = "footprinting-piglet-feed-crop-select";

  const { mutate: mutateFeedInformation } = useMutateFootprintingPigletFeedInformation(
    farmId,
    year,
    keyedFeedInformations.payload.filter((f) => !!f.feedId),
    primaryDataId ?? ""
  );

  const markFormPopulated = useSaveFormOnUnmount(mutateFeedInformation, !readOnly && !isLoading, !primaryDataId);

  useEffect(() => {
    if (keyedFeedInformations.entries.length === 0 || keyedFeedInformations.entries.some((f) => !f.id)) return;
    formRef.current?.validateFields();
  }, [keyedFeedInformations.entries]);

  useEffect(() => {
    if (!feedInformationQuery || !pigletFeedsQuery) {
      return;
    }

    const newFeedTypes: UploadablePigletFeedInformation[] = feedInformationQuery.map((f) => ({
      id: f.id,
      feedId: f.feedId ?? "",
      isSelfProduced: f.isSelfProduced ?? null,
      totalAmountFedTs: f.totalAmountFedTs ?? null,
      silagePct: f.silagePct ?? null,
      energyValue: f.energyValue ?? null,
      dryMatterPct: f.dryMatterPct ?? null,
      pigletsPct: f.pigletsPct ?? null,
      sowsPct: f.sowsPct ?? null,
      emissionFactorKgCo2Eq: f.emissionFactorKgCo2Eq ?? null,
    }));

    markFormPopulated();

    // Each entry mounts its fields fresh under its own `clientKey`, so `initialValue` seeds the form without a manual sync

    keyedFeedInformations.actions.hydrate(newFeedTypes.length > 0 ? newFeedTypes : [createFeedTypePlaceholder()]);
  }, [feedInformationQuery, pigletFeedsQuery, markFormPopulated, keyedFeedInformations.actions]);

  const isAnyFeedTypeSilage = keyedFeedInformations.entries.some((feed) => pigletFeedsQuery?.find((f) => f.id === feed.feedId)?.isSilage ?? false);

  const feedValidationPiglets = useMemo(() => {
    const avgLitterSize = pigletFootprint?.footprint?.averageLitterSize ?? 0;
    const avgLitterNumber = pigletFootprint?.footprint?.averageNumberOfLitters ?? 0;
    const endWeightKgs = pigletFootprint?.footprint?.averageSalesWeightKg ?? 0;
    const numberOfSows = pigletFootprint?.footprint?.herdDetails.averageNumberOfSows ?? 0;
    if ([avgLitterSize, avgLitterNumber, endWeightKgs, numberOfSows].includes(0)) return null;

    const weightGainAvgKgs = endWeightKgs - SOW_AND_PIGLET_DEFAULTS.avgPigletWeightAtWeaningEndKg;
    if (weightGainAvgKgs <= 0) return null;

    // avgLitterSize is piglets born alive, so the losses before weaning come off before we count the
    // piglets that actually eat this feed. A loss rate of zero is valid, hence no zero guard above.
    const sucklingPigletLossRatePct = pigletFootprint?.footprint?.herdDetails.sucklingPigletLossRatePct ?? 0;
    const weanedPigletsPerLitter = avgLitterSize * (1 - sucklingPigletLossRatePct);
    const pigletsProduced = weanedPigletsPerLitter * avgLitterNumber * numberOfSows;

    const pigletFeed = keyedFeedInformations.entries.map((fi) => {
      const amountKgs = (fi.totalAmountFedTs ?? 0) * KG_PER_TONNE * (fi.pigletsPct ?? 0);
      return { amountKgs, dryMatterKgs: amountKgs * (fi.dryMatterPct ?? DEFAULT_DRY_MATTER_PERCENTAGE), energyMjPerKg: fi.energyValue ?? 0 };
    });

    // FCR is compared against an as-fed industry corridor, so it stays on as-fed mass; energy needs the
    // dry-matter-corrected mass since energyMjPerKg is per kg dry matter.
    const totalFeedKgs = pigletFeed.reduce((totalKgs, { amountKgs }) => totalKgs + amountKgs, 0);
    if (totalFeedKgs === 0) return null;

    const totalEnergy = pigletFeed.reduce((sum, { dryMatterKgs, energyMjPerKg }) => sum + dryMatterKgs * energyMjPerKg, 0);
    const energyPerKgGainPerPiglet = totalEnergy / weightGainAvgKgs / pigletsProduced;
    const feedConversionRatio = totalFeedKgs / pigletsProduced / weightGainAvgKgs;

    const energyPerKgGainPerPigletInputState = inputStateForValue(energyPerKgGainPerPiglet, EXPECTED_FEED_ENERGY_PER_KG_GAIN_PER_PIGLET);
    const feedConversionRatioInputState = inputStateForValue(feedConversionRatio, EXPECTED_PIGLET_FCR);

    return {
      pigletsProduced,
      totalEnergy,
      weightGainAvgKgs,
      energyPerKgGainPerPiglet,
      feedConversionRatio,
      isExpected: energyPerKgGainPerPigletInputState === INPUT_STATE.expected && feedConversionRatioInputState === INPUT_STATE.expected,
      isTooHigh: TOO_HIGH_INPUT_STATES.includes(energyPerKgGainPerPigletInputState) || TOO_HIGH_INPUT_STATES.includes(feedConversionRatioInputState),
    };
  }, [
    keyedFeedInformations.entries,
    pigletFootprint?.footprint?.averageLitterSize,
    pigletFootprint?.footprint?.averageNumberOfLitters,
    pigletFootprint?.footprint?.averageSalesWeightKg,
    pigletFootprint?.footprint?.herdDetails.averageNumberOfSows,
    pigletFootprint?.footprint?.herdDetails.sucklingPigletLossRatePct,
  ]);

  const feedValidationSows = useMemo(() => {
    const numberOfSows = pigletFootprint?.footprint?.herdDetails.averageNumberOfSows ?? 0;
    const littersPerYear = pigletFootprint?.footprint?.averageNumberOfLitters ?? 0;
    const litterSize = pigletFootprint?.footprint?.averageLitterSize ?? 0;
    if ([numberOfSows, littersPerYear, litterSize].includes(0)) return null;

    const totalFeedEnergySows = keyedFeedInformations.entries
      .map((fi) => ({
        dryMatterKgs: (fi.totalAmountFedTs ?? 0) * KG_PER_TONNE * (fi.dryMatterPct ?? DEFAULT_DRY_MATTER_PERCENTAGE) * (fi.sowsPct ?? 0),
        energyMjPerKg: fi.energyValue ?? 0,
      }))
      .reduce((sum, { dryMatterKgs, energyMjPerKg }) => sum + dryMatterKgs * energyMjPerKg, 0);

    const feedEnergyPerSow = totalFeedEnergySows / numberOfSows;

    // Calculate total energy used for lactation. This intentionally uses the raw born alive litterSize
    // rather than the weaned count the piglet check derives — the sow nurses the whole litter.
    const lactationEnergyMj =
      SOW_AND_PIGLET_DEFAULTS.lactationEnergyBase +
      SOW_AND_PIGLET_DEFAULTS.milkEnergyPerKgLitterGainMJ * litterSize * SOW_AND_PIGLET_DEFAULTS.avgPigletWeightGainKg;

    // Calcualte daily energy requirement when the sow is neither breastfeeding nor pregnant
    const cycleDays = 365 / littersPerYear;
    const nonUseDaysEnergy =
      SOW_AND_PIGLET_DEFAULTS.emptyDailyMJ * Math.max(0, cycleDays - SOW_AND_PIGLET_DEFAULTS.totalGestationDays - SOW_AND_PIGLET_DEFAULTS.lactationDays);

    // Total energy used between pregnancy, breastfeeding, and non-use days
    const energyUsedPerCycle = SOW_AND_PIGLET_DEFAULTS.gestationEnergyRequirementBase + lactationEnergyMj + nonUseDaysEnergy;
    const totalYearlyEnergyRequirementPerSow = energyUsedPerCycle * littersPerYear;

    const energyRequirementRatioInputState = inputStateForValue(feedEnergyPerSow / totalYearlyEnergyRequirementPerSow, EXPECTED_SOW_ENERGY_REQUIREMENT_RATIO);

    return {
      energyPerSow: feedEnergyPerSow,
      requirementPerSow: totalYearlyEnergyRequirementPerSow,
      isExpected: energyRequirementRatioInputState === INPUT_STATE.expected,
      isTooHigh: TOO_HIGH_INPUT_STATES.includes(energyRequirementRatioInputState),
    };
  }, [
    keyedFeedInformations.entries,
    pigletFootprint?.footprint?.averageLitterSize,
    pigletFootprint?.footprint?.averageNumberOfLitters,
    pigletFootprint?.footprint?.herdDetails.averageNumberOfSows,
  ]);

  if (primaryDataId && isLoading) return <PageSpin />;

  return (
    <Flex vertical gap={16}>
      <Card dataTestId="footprinting-piglet-feed-crops-form">
        <Form ref={formRef} onValuesChange={markFormPopulated}>
          <Flex vertical gap={16}>
            <Flex vertical className="padding-100 padding-left-0" gap={8}>
              <Typography variant="h3">{t("pigletFeedInformationForm.header")}</Typography>
              <Typography variant="paragraph-m" color="grey-600" className="margin-0">
                {t("pigletFeedInformationForm.description")}
              </Typography>
            </Flex>

            <Flex vertical gap={16}>
              {keyedFeedInformations.entries.map((feedInfo, index) => {
                const selectGroupWithId = `${selectGroupName}-${index}`;
                const { clientKey } = feedInfo;
                const updateItem = (item: Partial<UploadablePigletFeedInformation>) => {
                  keyedFeedInformations.actions.update(clientKey, item);
                };

                const handleFeedDistributionChange = (value: number | null, target: "piglets" | "sows") => {
                  const pct = formatPercentageInput(value);

                  if (pct === null) {
                    updateItem({ ...feedInfo, pigletsPct: null, sowsPct: null });
                    formRef.current?.setFieldValue(`pigletsPct-${clientKey}`, null);
                    formRef.current?.setFieldValue(`sowsPct-${clientKey}`, null);
                    return;
                  }

                  if (target === "piglets") {
                    updateItem({ ...feedInfo, pigletsPct: pct, sowsPct: 1 - pct });
                    formRef.current?.setFieldValue(`pigletsPct-${clientKey}`, formatFractionInput(pct));
                    formRef.current?.setFieldValue(`sowsPct-${clientKey}`, formatFractionInput(1 - pct));
                  } else {
                    updateItem({ ...feedInfo, sowsPct: pct, pigletsPct: 1 - pct });
                    formRef.current?.setFieldValue(`pigletsPct-${clientKey}`, formatFractionInput(1 - pct));
                    formRef.current?.setFieldValue(`sowsPct-${clientKey}`, formatFractionInput(pct));
                  }
                };

                const selectedFeedType = pigletFeedsQuery?.find((ft) => ft.id === feedInfo.feedId);
                const isCustomFeed = selectedFeedType?.isCustom ?? false;
                const isSilage = selectedFeedType?.isSilage ?? false;

                return (
                  <Card key={clientKey} className="feed-cards padding-200">
                    <Flex vertical gap={16}>
                      <Flex justify="space-between" align="center" vertical={false}>
                        <Typography variant="h5" style={{ flex: 1 }}>
                          {selectedFeedType
                            ? t("pigletFeedInformationForm.feedTypeCount", {
                                count: index + 1,
                                cropName: t(`piglet:feedTypes.${selectedFeedType.translationSlug}`, { defaultValue: selectedFeedType.translationSlug }),
                              })
                            : t("pigletFeedInformationForm.feedTypeCountNoName", { count: index + 1 })}
                        </Typography>
                        {isCustomFeed && <Tag text={t("pigletFeedInformationForm.question.customFeed")} colour="blue" size="small" />}
                        <IconButton
                          type="tertiary"
                          disabled={readOnly}
                          dataTestId={`delete-feed-crop-${index}`}
                          size="small"
                          iconName="tabler/trash"
                          onClick={() => keyedFeedInformations.actions.remove(clientKey)}
                        />
                      </Flex>

                      <Flex gap={16} align="center">
                        <Form.Item className="flex-1">
                          <Select
                            showSearch
                            disabled={readOnly}
                            name={`${selectGroupName}-${clientKey}`}
                            dataTestId={selectGroupWithId}
                            label={t("pigletFeedInformationForm.question.feedType")}
                            rules={[{ required: true, message: "" }]}
                            initialValue={feedInfo.feedId ?? ""}
                            value={feedInfo.feedId ?? ""}
                            onChange={(selectedFeedId) => {
                              // This .find cannot fail but typescript doesn't know that
                              const feedType = pigletFeedsQuery?.find((feedType) => feedType.id === selectedFeedId);
                              const { energyValue } = getEnergyValuesFromFeedType(feedType);

                              // Custom Feed is the one type with no dryMatterPerFeedTypePct behind it. Left blank, both the
                              // energy checks below and the calculation in peas fall back to 88% without the farmer ever seeing
                              // it — and 88% is roughly three times wrong for a silage. Seeding the field puts the assumption
                              // on screen where it can be corrected instead of being applied silently.
                              const dryMatterPct = feedType?.dryMatterPerFeedTypePct ?? (feedType?.isCustom ? DEFAULT_DRY_MATTER_PERCENTAGE : null);

                              updateItem({
                                ...feedInfo,
                                feedId: feedType?.id ?? "",
                                energyValue,
                                silagePct: null,
                                dryMatterPct,
                                totalAmountFedTs: feedType?.isSilage ? 0 : feedInfo.totalAmountFedTs,
                                emissionFactorKgCo2Eq: null,
                              });

                              formRef.current?.setFieldsValue({
                                [`energyValue-${clientKey}`]: energyValue,
                                [`dryMatterPct-${clientKey}`]: formatFractionInput(dryMatterPct),
                                [`silagePct-${clientKey}`]: null,
                                [`totalAmountFed-${clientKey}`]: feedType?.isSilage ? 0 : feedInfo.totalAmountFedTs,
                                [`emissionFactorKgCo2Eq-${clientKey}`]: null,
                              });
                            }}
                            dropdownStyle={{ zIndex: 10010 }}
                          >
                            {(pigletFeedsQuery ?? []).map((feedType) => {
                              // Check if this feed type ID is already used in another item (Custom Feed is exempt — multiple rows are allowed)
                              const isAlreadyUsed =
                                !feedType.isCustom && keyedFeedInformations.entries.some((f) => f.feedId === feedType.id && f.feedId !== feedInfo.feedId);

                              return (
                                <Option
                                  disabled={readOnly || isAlreadyUsed}
                                  key={`${selectGroupWithId}-${feedType.id}`}
                                  data-testid={`${selectGroupWithId}-${feedType.id}`}
                                  value={feedType.id}
                                >
                                  {t(`piglet:feedTypes.${feedType.translationSlug}`, { defaultValue: feedType.translationSlug })}
                                </Option>
                              );
                            })}
                          </Select>
                        </Form.Item>

                        <Flex vertical align="center" gap={8}>
                          <Typography variant="paragraph-s" className="margin-0">
                            {t("pigletFeedInformationForm.question.isSelfProduced")}
                          </Typography>
                          <Switch
                            disabled={readOnly}
                            // dataTestId={`footprinting-piglet-feed-is-selfProduced-${index}`}
                            value={feedInfo.isSelfProduced ?? false}
                            onChange={(v) => updateItem({ ...feedInfo, isSelfProduced: v })}
                          />
                        </Flex>
                      </Flex>

                      {isSilage ? (
                        <InputNumber
                          disabled={readOnly}
                          dataTestId={`footprinting-piglet-feed-silage-pct-${index}`}
                          name={`silagePct-${clientKey}`}
                          label={t("pigletFeedInformationForm.question.silagePct")}
                          suffix="%"
                          value={formatFractionInput(feedInfo.silagePct ?? null)}
                          initialValue={formatFractionInput(feedInfo.silagePct ?? null)}
                          onChange={(v) => {
                            updateItem({ ...feedInfo, silagePct: formatPercentageInput(v) });
                            setTimeout(() => {
                              keyedFeedInformations.entries.forEach((f) => {
                                if (pigletFeedsQuery?.find((ft) => ft.id === f.feedId)?.isSilage) {
                                  formRef.current?.validateFields([`silagePct-${f.clientKey}`]);
                                }
                              });
                            }, 0);
                          }}
                          precision={2}
                          rules={[
                            {
                              required: true,
                              validator: (_: unknown, v: number) => {
                                if (isNullOrUndefined(v) || !isValidPercentageDecimal(v)) {
                                  return Promise.reject(new Error(t("pigletFeedInformationForm.error.validPercentage")));
                                }
                                const total = keyedFeedInformations.entries.reduce((sum, f, i) => {
                                  const value = i === index ? formatPercentageInput(v) ?? 0 : f.silagePct ?? 0;
                                  return sum + value;
                                }, 0);
                                if (Math.round(total * 100) > 100) {
                                  return Promise.reject(new Error(t("pigletFeedInformationForm.error.combinedSilagePercentage")));
                                }
                                return Promise.resolve();
                              },
                            },
                          ]}
                        />
                      ) : (
                        <InputNumber
                          disabled={readOnly}
                          dataTestId={`footprinting-piglet-feed-amount-input-${index}`}
                          name={`totalAmountFed-${clientKey}`}
                          label={t("pigletFeedInformationForm.question.totalAmountFed")}
                          suffix={t("pigletFeedInformationForm.question.totalAmountFedUnit")}
                          value={feedInfo.totalAmountFedTs}
                          initialValue={feedInfo.totalAmountFedTs}
                          onChange={(v) => updateItem({ ...feedInfo, totalAmountFedTs: v })}
                          rules={[
                            {
                              required: true,
                              validator: (_: unknown, v: number) => {
                                if (isNullOrUndefined(v) || !isValidPositiveNumber(v)) {
                                  return Promise.reject(new Error(t("pigletFeedInformationForm.error.validAmount")));
                                }
                                return Promise.resolve();
                              },
                            },
                          ]}
                        />
                      )}

                      <Typography variant="h6">{t("pigletFeedInformationForm.basicInformation")}</Typography>

                      <Form.Item
                        validateStatus={
                          feedInfo.energyValue
                            ? HARD_INPUT_STATES.includes(inputStateForValue(feedInfo.energyValue, pigletPracticeQuery.data?.feedEnergyMJNEL))
                              ? "error"
                              : "success"
                            : "success"
                        }
                      >
                        <InputNumber
                          disabled={readOnly}
                          dataTestId={`footprinting-piglet-feed-energy-input-${index}`}
                          name={`energyValue-${clientKey}`}
                          label={t("pigletFeedInformationForm.question.energyValue")}
                          suffix="MJ ME"
                          value={feedInfo.energyValue}
                          initialValue={feedInfo.energyValue}
                          onChange={(v) => updateItem({ ...feedInfo, energyValue: v })}
                          guidanceText={pigletPracticeQuery.data?.feedEnergyMJNEL}
                          rules={[
                            {
                              required: true,
                              message: t("pigletFeedInformationForm.error.validNumber", {
                                min: pigletPracticeQuery.data?.feedEnergyMJNEL?.[1],
                                max: pigletPracticeQuery.data?.feedEnergyMJNEL?.[2],
                              }),
                            },
                          ]}
                        />
                      </Form.Item>
                      {feedInfo.energyValue ? <SanityCheck inputValue={feedInfo.energyValue} inputRange={pigletPracticeQuery.data?.feedEnergyMJNEL} /> : null}

                      <InputNumber
                        disabled={readOnly}
                        dataTestId={`footprinting-piglet-feed-drymatter-input-${index}`}
                        name={`dryMatterPct-${clientKey}`}
                        label={t("pigletFeedInformationForm.question.dryMatterPct")}
                        suffix="%"
                        value={formatFractionInput(feedInfo.dryMatterPct ?? null)}
                        initialValue={formatFractionInput(feedInfo.dryMatterPct ?? null)}
                        onChange={(v) => updateItem({ ...feedInfo, dryMatterPct: formatPercentageInput(v) })}
                        precision={2}
                        rules={[
                          {
                            required: true,
                            validator: (_: unknown, v: number) => {
                              if (isNullOrUndefined(v) || !isValidPercentageDecimal(v)) {
                                return Promise.reject(new Error(t("pigletFeedInformationForm.error.validPercentage")));
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      />

                      {isCustomFeed && (
                        <InputNumber
                          disabled={readOnly}
                          dataTestId={`footprinting-piglet-feed-emission-factor-input-${index}`}
                          name={`emissionFactorKgCo2Eq-${clientKey}`}
                          label={t("pigletFeedInformationForm.question.emissionFactor")}
                          tooltip={t("pigletFeedInformationForm.tooltip.emissionFactor")}
                          suffix={t("pigletFeedInformationForm.question.emissionFactorUnit")}
                          value={feedInfo.emissionFactorKgCo2Eq}
                          initialValue={feedInfo.emissionFactorKgCo2Eq}
                          onChange={(v) => updateItem({ ...feedInfo, emissionFactorKgCo2Eq: v })}
                          precision={4}
                          rules={[
                            {
                              required: true,
                              validator: (_: unknown, v: number) => {
                                if (isNullOrUndefined(v) || !isValidNonnegativeNumber(v)) {
                                  return Promise.reject(new Error(t("pigletFeedInformationForm.error.validEmissionFactor")));
                                }
                                return Promise.resolve();
                              },
                            },
                          ]}
                        />
                      )}

                      <Flex vertical>
                        <Typography variant="h6">{t("pigletFeedInformationForm.feedDistribution")}</Typography>
                        <Typography variant="paragraph-m" color="grey-600" className="margin-0">
                          {t("pigletFeedInformationForm.feedDistributionDescription")}
                        </Typography>
                      </Flex>

                      <Form.Item>
                        <InputImageCard
                          disabled={readOnly}
                          title={t("pigletFeedInformationForm.question.pigletsPct")}
                          tooltip={t("pigletFeedInformationForm.tooltip.pigletsPct")}
                          suffix="%"
                          name={`pigletsPct-${clientKey}`}
                          value={formatFractionInput(feedInfo.pigletsPct ?? null)}
                          initialValue={formatFractionInput(feedInfo.pigletsPct ?? null)}
                          onChange={(v) => {
                            handleFeedDistributionChange(v, "piglets");
                            setTimeout(() => {
                              keyedFeedInformations.entries.forEach((f) => {
                                formRef.current?.validateFields([`pigletsPct-${f.clientKey}`, `sowsPct-${f.clientKey}`]);
                              });
                            }, 0);
                          }}
                          precision={2}
                          image={{
                            imageAlt: "Piglet image",
                            imageSrc: "https://klimpublic.s3.eu-central-1.amazonaws.com/footprinting-crop-images/small/piglet.webp",
                          }}
                          rules={[
                            {
                              required: true,
                              validator: (_: unknown, v: number) => {
                                if (isNullOrUndefined(v) || !isValidPercentageDecimal(v)) {
                                  return Promise.reject();
                                }

                                if (v / 100 + (feedInfo.sowsPct ?? 0) > 1) {
                                  return Promise.reject();
                                }

                                return Promise.resolve();
                              },
                            },
                          ]}
                        />
                      </Form.Item>

                      <Form.Item>
                        <InputImageCard
                          disabled={readOnly}
                          title={t("pigletFeedInformationForm.question.sowsPct")}
                          tooltip={t("pigletFeedInformationForm.tooltip.sowsPct")}
                          suffix="%"
                          name={`sowsPct-${clientKey}`}
                          value={formatFractionInput(feedInfo.sowsPct ?? null)}
                          initialValue={formatFractionInput(feedInfo.sowsPct ?? null)}
                          onChange={(v) => {
                            handleFeedDistributionChange(v, "sows");
                            setTimeout(() => {
                              keyedFeedInformations.entries.forEach((f) => {
                                formRef.current?.validateFields([`pigletsPct-${f.clientKey}`, `sowsPct-${f.clientKey}`]);
                              });
                            }, 0);
                          }}
                          precision={2}
                          image={{
                            imageAlt: "Sow image",
                            imageSrc: "https://klimpublic.s3.eu-central-1.amazonaws.com/footprinting-crop-images/small/pork.webp",
                          }}
                          rules={[
                            {
                              required: true,
                              validator: (_: unknown, v: number) => {
                                if (isNullOrUndefined(v) || !isValidPercentageDecimal(v)) {
                                  return Promise.reject();
                                }

                                if (v / 100 + (feedInfo.pigletsPct ?? 0) > 1) {
                                  return Promise.reject();
                                }

                                return Promise.resolve();
                              },
                            },
                          ]}
                        />
                      </Form.Item>

                      {feedInfo.pigletsPct === null ||
                      feedInfo.sowsPct === null ||
                      isPercentageComplete((feedInfo.pigletsPct ?? 0) + (feedInfo.sowsPct ?? 0)) ? null : (
                        <AlertBanner
                          type="error"
                          iconName="tabler/alert-triangle"
                          iconAlign="start"
                          iconSize={24}
                          gap={4}
                          headingSize="h6"
                          description={t("pigletFeedInformationForm.error.invalidDistributionDescription")}
                          title={
                            (feedInfo.pigletsPct ?? 0) + (feedInfo.sowsPct ?? 0) > 1
                              ? t("pigletFeedInformationForm.error.invalidDistributionTitleTooHigh")
                              : t("pigletFeedInformationForm.error.invalidDistributionTitleTooLow")
                          }
                        />
                      )}
                    </Flex>
                  </Card>
                );
              })}
            </Flex>
            {isAnyFeedTypeSilage && (
              <ProgressBarCardWithContent
                currentValue={keyedFeedInformations.entries.filter((fi) => fi.feedId).reduce((sum, crop) => sum + (crop.silagePct ?? 0) * 100, 0)}
                title={t("pigletFeedInformationForm.silagePercentageSum.title")}
                showPercentValue={true}
                showRemainingPercentage={true}
                onAnimationFinish={() => {}}
                errorMessage={t("pigletFeedInformationForm.silagePercentageSum.errorMessage")}
                successMessage={t("pigletFeedInformationForm.silagePercentageSum.successMessage")}
                infoMessage={t("pigletFeedInformationForm.silagePercentageSum.description")}
              />
            )}
            <Button
              type="text"
              dataTestId="add-feed-crop-button"
              disabled={readOnly}
              onClick={() => {
                keyedFeedInformations.actions.add(createFeedTypePlaceholder());
              }}
            >
              <Icon iconName="klim/plus" className="icon inline" /> {t("pigletFeedInformationForm.addFeedType")}
            </Button>
          </Flex>
        </Form>
      </Card>

      {keyedFeedInformations.entries.length && feedValidationPiglets ? (
        <FeedEnergyCheckCard
          dataTestId="footprinting-piglet-feed-energy-check-card"
          title={t("pigletFeedInformationForm.pigletFeedEnergyTitle")}
          description={t("pigletFeedInformationForm.pigletFeedEnergyDescription")}
          isExpected={feedValidationPiglets.isExpected}
          alertTitle={
            feedValidationPiglets.isExpected
              ? t("pigletFeedInformationForm.success.feedAmountInRangePiglet")
              : feedValidationPiglets.isTooHigh
              ? t("pigletFeedInformationForm.error.feedAmountTooHighPiglet")
              : t("pigletFeedInformationForm.error.feedAmountTooLowPiglet")
          }
          alertDescription={
            feedValidationPiglets.isExpected
              ? t("pigletFeedInformationForm.success.feedValidationDescriptionInRangePiglet")
              : feedValidationPiglets.isTooHigh
              ? t("pigletFeedInformationForm.error.feedValidationDescriptionHighPiglet")
              : t("pigletFeedInformationForm.error.feedValidationDescriptionLowPiglet")
          }
        />
      ) : null}

      {keyedFeedInformations.entries.length && feedValidationSows ? (
        <FeedEnergyCheckCard
          dataTestId="footprinting-sow-feed-energy-check-card"
          title={t("pigletFeedInformationForm.sowFeedEnergyTitle")}
          description={t("pigletFeedInformationForm.sowFeedEnergyDescription")}
          isExpected={feedValidationSows.isExpected}
          alertTitle={
            feedValidationSows.isExpected
              ? t("pigletFeedInformationForm.success.feedAmountInRangeSow")
              : feedValidationSows.isTooHigh
              ? t("pigletFeedInformationForm.error.feedAmountTooHighSow")
              : t("pigletFeedInformationForm.error.feedAmountTooLowSow")
          }
          alertDescription={
            feedValidationSows.isExpected
              ? t("pigletFeedInformationForm.success.feedValidationDescriptionInRangeSow")
              : feedValidationSows.isTooHigh
              ? t("pigletFeedInformationForm.error.feedValidationDescriptionHighSow")
              : t("pigletFeedInformationForm.error.feedValidationDescriptionLowSow")
          }
        />
      ) : null}
    </Flex>
  );
};

PigletFeedInformationForm.Key = "piglet.feedInformation";
export { PigletFeedInformationForm };
